import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import { Row, Col } from 'react-styled-flexboxgrid';
import Button from '../../Common/Button';
import { linkStyles } from '../../Common/Buttons/TrackedLink';
import LazyImage from '../../Common/LazyImage';

import typography from '../../../styling/mixins/typography';
import { PadComponent } from '../../../styling/layout-containers/LandingPageContainers';
import {
  MOBILE_END_QUERY,
  DESKTOP_START_QUERY,
  DESKTOP_MEDIUM_START_QUERY,
  DESKTOP_LARGE_START_QUERY,
} from '../../../styling/mixins/responsive';

import HandOffLink from '../../../utils/urls/HandOffLink';

import withTrackingPropsProvider from '../../../hocs/withTrackingPropsProvider';

const SpacedRow = styled(Row)`
  ${({ theme }) => PadComponent(theme)} margin-top: -1.5rem;
  margin-bottom: 1.5rem;
`;

const SpacedCol = styled(Col)`
  text-align: center;
  margin-top: 1.25rem;

  @media (${DESKTOP_MEDIUM_START_QUERY}) {
    margin-top: 1.5rem;
  }

  &:hover {
    cursor: pointer;
  }
`;

const BackgroundContainer = styled(HandOffLink)`
  background-color: ${({ backgroundColor }) =>
    backgroundColor ? '#' + backgroundColor : 'white'};
  width: 100%;

  display: flex;
  flex-direction: column;
  max-height: 480px;

  /* IE11 hack, fixes issue with displaying large tiles at mobile browser sizes */
  @media all and (${MOBILE_END_QUERY}) and (-ms-high-contrast: active),
    all and (${MOBILE_END_QUERY}) and (-ms-high-contrast: none) {
    flex-direction: row;
  }
`;

const TextContainer = styled.div`
  width: 100%;
  text-align: center;
  display: none;
  flex: 0 1 auto;
  padding: 0 1rem;

  @media (${DESKTOP_START_QUERY}) {
    display: block;
    padding-top: 2.5rem;
  }

  @media (${DESKTOP_LARGE_START_QUERY}) {
    padding-top: 3rem;
  }
`;

const Title = styled.h3`
  ${typography.BasisFont};
  font-size: 1rem;
  font-weight: 500;
  margin: 0;

  @media (${DESKTOP_START_QUERY}) {
    font-size: 1.5rem;
    padding-bottom: 0.5rem;
  }

  @media (${DESKTOP_LARGE_START_QUERY}) {
    font-size: 2rem;
    padding-bottom: 0.75rem;
  }
`;

const FakeLink = styled.div`
  ${linkStyles};
`;

const StyledLink = styled(Button)`
  max-width: 95%;
  overflow: hidden;
  padding: 0 0 2px 0;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MobileLinkContainer = styled.div`
  padding-top: 0.5rem;

  @media (${DESKTOP_START_QUERY}) {
    display: none;
  }
`;

const ImageContainer = styled.div`
  flex: 1 1 auto;
  margin-top: 1.5rem;
  position: relative;

  @media (${DESKTOP_START_QUERY}) {
    margin: 0;
    height: 20rem;
  }
`;

const Image = styled(LazyImage)`
  display: block;
  width: 100%;
  height: inherit;
  object-fit: contain;
  object-position: bottom;

  /* IE11 hack, fixes issue with the images at desktop browser sizes stretching vertical */
  @media all and (${DESKTOP_START_QUERY}) and (-ms-high-contrast: active),
    all and (${DESKTOP_START_QUERY}) and (-ms-high-contrast: none) {
    height: auto;
    position: absolute;
    bottom: 0;
  }
`;

const RightAlignedImage = styled(Image)`
  object-position: right bottom;

  /* IE11 hack, fixes issue with the images at desktop browser sizes stretching vertical */
  @media all and (${DESKTOP_START_QUERY}) and (-ms-high-contrast: active),
    all and (${DESKTOP_START_QUERY}) and (-ms-high-contrast: none) {
    right: 0;
  }
`;

class OffsetCategoryMosaic extends Component {
  static propTypes = {
    isMobile: PropTypes.bool.isRequired,
    categories: PropTypes.arrayOf(
      PropTypes.shape({
        backgroundColor: PropTypes.string,
        title: PropTypes.string.isRequired,
        cta: PropTypes.string.isRequired,
        href: PropTypes.string.isRequired,
        image: PropTypes.string.isRequired,
        mobileImage: PropTypes.string.isRequired,
      }),
    ),
  };

  render() {
    let { categories, isMobile } = this.props;

    let elements = categories.map((category, index, array) => {
      let xs, md, ImageType;

      if (index === 0 || index === array.length - 1) {
        xs = 12;
        md = 8;
        ImageType = Image;
      } else {
        xs = 6;
        md = 4;
        ImageType = RightAlignedImage;
      }

      return (
        <SpacedCol xs={xs} md={md} key={category.title} cta={category.title}>
          <BackgroundContainer
            href={category.href}
            identifier={category.title}
            backgroundColor={category.backgroundColor}
          >
            <TextContainer>
              <Title>{category.title}</Title>
              <FakeLink>{category.cta}</FakeLink>
            </TextContainer>
            <ImageContainer>
              <ImageType
                alt={category.title}
                src={isMobile ? category.mobileImage : category.image}
              />
            </ImageContainer>
          </BackgroundContainer>
          <MobileLinkContainer>
            <StyledLink href={category.href} type="TrackedLink" color="lapel">
              {category.title}
            </StyledLink>
          </MobileLinkContainer>
        </SpacedCol>
      );
    });

    return <SpacedRow>{elements}</SpacedRow>;
  }
}

export default withTrackingPropsProvider(OffsetCategoryMosaic);
